import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSpotUser } from "@/utils/spotUser";
import {
  KBSyncService,
  type SyncStatusResponse,
  type EnableSyncRequest,
  type UpdateSyncRequest,
} from "@/services/kbSync";
import { SpotToast } from "@/components/SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useMemo, useEffect, useRef } from "react";

export function useKBSyncList(kbId: string, enabled = true) {
  const { spotGroup } = useSpotUser();
  const hasUnresolvedRef = useRef(false);

  const query = useQuery<SyncStatusResponse[]>({
    queryKey: [DATA_KEYS.KB_SYNCS, spotGroup.id, kbId],
    queryFn: async () => {
      const res = await KBSyncService.listSyncs(spotGroup.id, kbId);
      const data = res.data ?? [];
      // Keep polling while either:
      //  - any sync hasn't completed its first poll yet (no last_sync_at), or
      //  - any watched_item is still missing kb_doc_id (the docstore
      //    link runs at ingest time but the KB doc may not exist
      //    yet when ingestion is async — once it lands, the watched
      //    item gets stamped and the FE picks it up here).
      // Both cases need fast-ish refetches so the sync icon lights
      // up promptly without a manual refresh.
      hasUnresolvedRef.current = data.some(
        (s) =>
          !s.last_sync_at || (s.watched_items ?? []).some((w) => !w.kb_doc_id)
      );
      return data;
    },
    retry: 1,
    enabled: enabled && !!kbId,
    refetchInterval: () => (hasUnresolvedRef.current ? 5000 : false),
  });

  return query;
}

export function useSyncedSourceTypes(kbId: string) {
  const { data: syncs } = useKBSyncList(kbId);
  return useMemo(() => {
    if (!syncs || syncs.length === 0) return new Set<string>();
    return new Set(syncs.map((s) => s.source_type));
  }, [syncs]);
}

export function useEnableSyncMutation(kbId: string) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: EnableSyncRequest) =>
      KBSyncService.enableSync(spotGroup.id, kbId, body),
    onSuccess: (_data, body) => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.KB_SYNCS] });
      // Backend stamps ``m_source_external_id`` (and m_source_type) on
      // the watched folder doc when sync is enabled on an existing
      // doc, so the docs query needs a refetch for the FE to pick up
      // the new fields the syncRootIndex relies on.
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
      });
      SpotToast.success("Sync enabled");
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_SYNC_ENABLE, {
        knowledge_base_id: kbId,
        source_type: body.source_type,
        schedule_frequency: body.schedule?.action,
      });
    },
    onError: () => {
      SpotToast.error("Failed to enable sync");
    },
  });
}

export function useUpdateSyncMutation(kbId: string) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      triggerId,
      body,
    }: {
      triggerId: string;
      body: UpdateSyncRequest;
    }) => KBSyncService.updateSync(spotGroup.id, kbId, triggerId, body),
    onSuccess: (_data, { triggerId, body }) => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.KB_SYNCS] });
      SpotToast.success("Sync settings updated");
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_SYNC_UPDATE, {
        knowledge_base_id: kbId,
        trigger_id: triggerId,
        schedule_frequency: body.schedule?.action,
      });
    },
    onError: () => {
      SpotToast.error("Failed to update sync settings");
    },
  });
}

export function useDisableSyncMutation(kbId: string) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (triggerId: string) =>
      KBSyncService.disableSync(spotGroup.id, kbId, triggerId),
    onSuccess: (_data, triggerId) => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.KB_SYNCS] });
      // Disabling sync drops the trigger; the docs themselves stay,
      // but the syncRootIndex needs a doc refetch so the icon
      // disappears from the explorer immediately.
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
      });
      SpotToast.success("Sync disabled");
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_SYNC_DISABLE, {
        knowledge_base_id: kbId,
        trigger_id: triggerId,
      });
    },
    onError: () => {
      SpotToast.error("Failed to disable sync");
    },
  });
}

export function useForceSyncMutation(kbId: string) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  return useMutation({
    mutationFn: (triggerId: string) =>
      KBSyncService.forceSync(spotGroup.id, kbId, triggerId),
    onSuccess: (_data, triggerId) => {
      SpotToast.success("Sync triggered");
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_SYNC_FORCE, {
        knowledge_base_id: kbId,
        trigger_id: triggerId,
      });
      // Invalidate ingestion runs immediately so the new run appears
      queryClient.invalidateQueries({
        queryKey: ["KNOWLEDGE_BASE_INGESTION_JOBS"],
      });
      // Clear any existing poll before starting a new one
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      // Poll sync list + ingestion runs for a few seconds to pick up status changes
      pollIntervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.KB_SYNCS] });
        queryClient.invalidateQueries({
          queryKey: ["KNOWLEDGE_BASE_INGESTION_JOBS"],
        });
      }, 2000);
      pollTimeoutRef.current = setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }, 15000);
    },
    onError: () => {
      SpotToast.error("Failed to trigger sync");
    },
  });
}
